import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type DryRunImportResult,
  type ListImportsResult,
} from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { parseCsv } from '@whinerp/contracts/csv';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-34 Import wizard.
 *
 * The rollback is the feature, and the dry run is what makes it survivable.
 * Loading four thousand rows from somebody's spreadsheet is a thing people do
 * once, nervously, and the nervousness is rational: a bad mapping puts a phone
 * number in the company field on every record.
 *
 * So nothing is written until the check has been read, and the errors are
 * shown in full rather than counted - "17 rows failed" is a number, and the
 * person needs the seventeen rows.
 */

type Preview = DryRunImportResult;

const IMPORT_OBJECTS = ['account', 'contact', 'lead'] as const;
const IMPORT_SOURCES = ['other', 'salesforce', 'hubspot', 'zoho', 'pipedrive'] as const;

function ImportScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const queryClient = useQueryClient();

  // What is being brought in, and where it came from (G-270): the source's own
  // column names map themselves, and the rest are named as left behind.
  const [objectKey, setObjectKey] = useState<(typeof IMPORT_OBJECTS)[number]>('account');
  const [source, setSource] = useState<(typeof IMPORT_SOURCES)[number]>('other');
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<Preview | null>(null);

  const parsed = parseCsv(csv);

  const fields = useQuery({
    queryKey: ['object-layout', objectKey],
    queryFn: () => api.getObjectLayout({ path: { objectKey } }),
  });

  const history = useQuery<ListImportsResult, ApiError>({
    queryKey: ['imports'],
    queryFn: () => api.listImports({}),
  });

  const suggest = useMutation({
    mutationFn: (input: { source: (typeof IMPORT_SOURCES)[number]; objectKey: (typeof IMPORT_OBJECTS)[number]; headers: string[] }) =>
      api.suggestImportMapping({ body: input }),
    onSuccess: (result) => {
      setMapping(result.mapping);
      setUnmatched(result.unmatched);
      setPreview(null);
    },
  });
  const remap = (next: { source?: (typeof IMPORT_SOURCES)[number]; objectKey?: (typeof IMPORT_OBJECTS)[number]; csv?: string }): void => {
    const headers = parseCsv(next.csv ?? csv).headers;
    if (headers.length > 0) suggest.mutate({ source: next.source ?? source, objectKey: next.objectKey ?? objectKey, headers });
  };

  const dryRun = useMutation({
    mutationFn: () =>
      api.dryRunImport({
        body: { objectKey, fileName: fileName || 'pasted.csv', mapping, rows: parsed.rows },
      }),
    onSuccess: (result) => setPreview(result),
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const apply = useMutation({
    mutationFn: (runId: string) => api.applyImport({ path: { runId }, body: { rows: parsed.rows } }),
    onSuccess: (result) => {
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      toast.show({
        tone: 'success',
        title: t('setup.import.apply', { values: { count: formatCount(result.created, format) } }),
        body: t('setup.import.undoUntil', {
          values: { when: formatDateTime(result.reversibleUntil, format) },
        }),
      });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const undo = useMutation({
    mutationFn: (runId: string) => api.reverseImport({ path: { runId }, body: {} }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
      // Says what came back out AND what did not, because a record somebody
      // edited after the import is deliberately left alone.
      toast.show({
        tone: 'info',
        title: t('setup.import.undo'),
        body: t('setup.import.undone', {
          values: {
            removed: formatCount(result.removed, format),
            kept: formatCount(result.keptBecauseEdited, format),
          },
        }),
      });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  return (
    <div data-component="ImportWizard" className="flex w-full flex-col gap-4">
      <PageHeading>{t('setup.import.title')}</PageHeading>

      <Panel data-step="upload">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.import.step.upload')}</SectionLabel>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('setup.import.object')}
              <select
                data-import-object
                value={objectKey}
                onChange={(event) => {
                  const next = event.target.value as (typeof IMPORT_OBJECTS)[number];
                  setObjectKey(next);
                  remap({ objectKey: next });
                }}
                className="h-input rounded-control border border-rule bg-surface px-2 text-body text-ink"
              >
                {IMPORT_OBJECTS.map((key) => <option key={key} value={key}>{t(`setup.import.object.${key}`)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('setup.import.source')}
              <select
                data-import-source
                value={source}
                onChange={(event) => {
                  const next = event.target.value as (typeof IMPORT_SOURCES)[number];
                  setSource(next);
                  remap({ source: next });
                }}
                className="h-input rounded-control border border-rule bg-surface px-2 text-body text-ink"
              >
                {IMPORT_SOURCES.map((key) => <option key={key} value={key}>{t(`setup.import.source.${key}`)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-dense text-ink">
              {t('setup.import.file')}
              <input
                data-import-file
                type="file"
                accept=".csv,text/csv"
                className="text-dense text-ink"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  void file.text().then((text) => {
                    setFileName(file.name);
                    setCsv(text);
                    setPreview(null);
                    remap({ csv: text });
                  });
                }}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('setup.field.name')}
            <input
              data-file-name
              value={fileName}
              onChange={(event) => setFileName(event.target.value)}
              className="h-input rounded-control border border-rule bg-surface px-3 text-body text-ink"
            />
          </label>
          <textarea
            data-csv
            rows={5}
            value={csv}
            aria-label={t('setup.import.step.upload')}
            onChange={(event) => {
              const before = parseCsv(csv).headers.join(',');
              setCsv(event.target.value);
              setPreview(null);
              // Re-suggested only when the header line changed, not on every keystroke in a row.
              if (parseCsv(event.target.value).headers.join(',') !== before) remap({ csv: event.target.value });
            }}
            className="w-full rounded-control border border-rule bg-surface p-2 font-mono text-dense text-ink"
          />
        </div>
      </Panel>

      {parsed.headers.length > 0 ? (
        <Panel data-step="map">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('setup.import.step.map')}</SectionLabel>
            {unmatched.length > 0 ? (
              <p data-import-unmatched className="text-meta text-warning-text">
                {t('setup.import.unmatched', { values: { columns: unmatched.join(', ') } })}
              </p>
            ) : null}
            <div className="w-full overflow-x-auto" data-scroll-container>
              <table className="w-full min-w-panel-wide">
                <tbody>
                  {parsed.headers.map((header) => (
                    <tr key={header} className="border-t border-t-rule">
                      <td className="py-1 pe-3 text-dense text-ink">{header}</td>
                      <td className="py-1 pe-3 text-meta text-ink-meta">
                        {parsed.rows[0]?.[header] ?? ''}
                      </td>
                      <td className="py-1">
                        <select
                          data-map={header}
                          aria-label={header}
                          value={mapping[header] ?? ''}
                          onChange={(event) => {
                            setMapping((current) => ({ ...current, [header]: event.target.value }));
                            setPreview(null);
                          }}
                          className="h-input w-full rounded-control border border-rule bg-surface px-2 text-dense text-ink"
                        >
                          <option value="">{t('field.clear')}</option>
                          {(fields.data?.fields ?? []).map((field) => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                            </option>
                          ))}
                          {/* A suggested field the layout does not show is still what will be written, so it is shown. */}
                          {mapping[header] && !(fields.data?.fields ?? []).some((field) => field.key === mapping[header]) ? (
                            <option value={mapping[header]}>{mapping[header]}</option>
                          ) : null}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              intent="secondary"
              data-dry-run
              disabled={dryRun.isPending}
              onClick={() => dryRun.mutate()}
            >
              {t('setup.import.dryRun')}
            </Button>
          </div>
        </Panel>
      ) : null}

      {preview ? (
        <Panel data-step="check">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('setup.import.step.check')}</SectionLabel>
            <Num data-valid-rows className="text-body text-ink">
              {t('setup.import.valid', {
                values: {
                  count: formatCount(preview.validRows, format),
                  total: formatCount(preview.totalRows, format),
                },
              })}
            </Num>

            {preview.errors.length > 0 ? (
              <div data-import-errors className="flex flex-col gap-1">
                <Num className="text-dense text-danger-text">
                  {t('setup.import.errors', {
                    values: { count: formatCount(preview.errors.length, format) },
                  })}
                </Num>
                {/* The first ten in place, and the whole lot downloadable. A
                    count alone is not something a person can act on. */}
                <ul className="flex flex-col">
                  {preview.errors.slice(0, 10).map((error, index) => (
                    <li key={index} className="text-meta text-ink-meta">
                      <Num>{formatCount(error.row, format)}</Num> {error.column ?? ''}:{' '}
                      {error.message}
                    </li>
                  ))}
                </ul>
                <a
                  data-download-errors
                  href={`data:text/csv;charset=utf-8,${encodeURIComponent(
                    ['row,column,message', ...preview.errors.map((e) => `${String(e.row)},${e.column ?? ''},"${e.message}"`)].join('\n'),
                  )}`}
                  download="import-errors.csv"
                  className="self-start text-dense text-bronze-text underline underline-offset-4"
                >
                  {t('setup.import.download')}
                </a>
              </div>
            ) : null}

            <Button
              intent="primary"
              data-apply-import
              disabled={apply.isPending || preview.validRows === 0}
              onClick={() => apply.mutate(preview.runId)}
            >
              {t('setup.import.apply', {
                values: { count: formatCount(preview.validRows, format) },
              })}
            </Button>
          </div>
        </Panel>
      ) : null}

      <Panel data-region="history">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.import.history')}</SectionLabel>
          {history.isPending ? (
            <Skeleton className="h-block-xs w-full" />
          ) : (history.data?.runs ?? []).length === 0 ? (
            <p className="text-body text-ink-meta">{t('setup.import.nothing')}</p>
          ) : (
            <ul className="flex flex-col">
              {(history.data?.runs ?? []).map((run) => (
                <li
                  key={run.runId}
                  data-import-run={run.runId}
                  className="flex flex-wrap items-center gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-dense text-ink">{run.fileName}</span>
                  <Num className="text-meta text-ink-meta">
                    {formatCount(run.createdRows, format)}
                  </Num>
                  <span className="text-meta text-ink-meta">{run.status}</span>
                  {run.reversibleUntil ? (
                    <Button
                      intent="secondary"
                      data-undo-import={run.runId}
                      disabled={undo.isPending}
                      onClick={() => undo.mutate(run.runId)}
                    >
                      {t('setup.import.undo')}
                    </Button>
                  ) : run.status === 'applied' ? (
                    // The window has closed, said in words rather than by the
                    // button quietly not being there.
                    <span className="text-meta text-ink-meta">
                      <Icon name="Clock" size="inline" decorative />
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>
    </div>
  );
}

function ImportEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Upload"
      title={t('setup.import.title')}
      body={t('setup.import.nothing')}
      primary={{ label: t('empty.action.chooseFile'), icon: 'Upload', href: '/setup/import' }}
    />
  );
}

function ImportLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-md" />
      <Skeleton className="h-block-md w-full" />
    </div>
  );
}

function ImportError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The import screen could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-34',
  route: '/setup/import',
  surface: 'page',
  title: 'Import',
  features: ['F-016'],
  entitlement: 'F-016',
  reachedBy: [{ kind: 'screen', from: 'SC-30', how: 'the Data card in Setup' }],
  components: [],
  elements: ['ImportWizard'],
  states: {
    empty: ImportEmpty,
    loading: ImportLoading,
    error: ImportError,
  },
  component: ImportScreen,
});

export default screen;
